import { computed, ref, shallowRef } from 'vue'
import { defineStore } from 'pinia'

import type { VendorEvent } from '@/utils/vendors/types'
import { teslaExtractor, getTeslaEventInfo } from '@/utils/vendors/tesla'
import {
  detectDriveProblem,
  groupEventsByDay,
  toRows,
  type DriveProblem
} from '@/utils/vendors/tesla-navigator'

function parseEvents(files: File[]): Promise<VendorEvent[]> {
  if (!teslaExtractor.parseEvents) throw new Error('Tesla extractor cannot parse events')
  return teslaExtractor.parseEvents(files)
}

export const useTeslaCamStore = defineStore('teslacam', () => {
  const folderName = ref<string | null>(null)
  const isParsing = ref(false)
  const problem = ref<DriveProblem | null>(null)
  // shallowRef: events hold File objects, which must not be wrapped in reactive proxies.
  const events = shallowRef<VendorEvent[]>([])

  const dayGroups = computed(() => groupEventsByDay(events.value))
  const rows = computed(() => toRows(dayGroups.value))
  const countsByCategory = computed(() => {
    const counts: Record<string, number> = {}
    for (const event of events.value) {
      const { category } = getTeslaEventInfo(event)
      if (category) counts[category] = (counts[category] ?? 0) + 1
    }
    return counts
  })

  function clear() {
    folderName.value = null
    problem.value = null
    events.value = []
  }

  /** Reads a picked folder. Browse only: nothing is uploaded or written to the drive. */
  async function loadFiles(files: File[], name: string) {
    clear()
    folderName.value = name
    isParsing.value = true
    try {
      problem.value = await detectDriveProblem(files)
      if (problem.value) return
      events.value = await parseEvents(files)
      if (events.value.length === 0) problem.value = 'empty'
    } catch (err) {
      console.error('TeslaCam parse error', err)
      problem.value = 'unreadable'
    } finally {
      isParsing.value = false
    }
  }

  return {
    folderName,
    isParsing,
    problem,
    events,
    dayGroups,
    rows,
    countsByCategory,
    loadFiles,
    clear
  }
})
